import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { requireAdminRoute } from "@/lib/require-admin-route";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { humanizeApiError } from "@/lib/form-errors";
import { useToast } from "@/components/ui/Toast";
import { useTopbar } from "@/components/chrome/use-topbar";
import { ZipRadiusRows, type DisplayRow } from "@/components/markets/ZipRadiusRows";
import { MarketMap } from "@/components/markets/MarketMap";
import type { ZipRadiusRowResponse } from "@/lib/market-radius-schemas";

interface MarketDetail {
  id: string;
  name: string;
  state: string;
  msa: string | null;
  zipsCsv: string | null;
  loanBandMinUsd: number;
  loanBandMaxUsd: number;
  termMonthsMin: number;
  termMonthsMax: number;
  status: "active" | "draft" | "paused";
  zipRadii?: ZipRadiusRowResponse[];
}

interface MarketPayload {
  name: string;
  state: string;
  msa?: string;
  zipsCsv?: string;
  loanBandMinUsd: number;
  loanBandMaxUsd: number;
  termMonthsMin: number;
  termMonthsMax: number;
  status: MarketDetail["status"];
  zipRadii?: { zip: string; radiusMi: number }[];
}

export const Route = createFileRoute("/_app/settings/markets/$id")({
  beforeLoad: requireAdminRoute,
  component: MarketDetailPage,
});

function MarketDetailPage() {
  const { id } = Route.useParams();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data, isLoading, error } = useQuery<MarketDetail>({
    queryKey: ["settings", "markets", id],
    queryFn: () => apiFetch<MarketDetail>(`/api/settings/markets/${id}`),
  });

  const [name, setName] = useState("");
  const [state, setState] = useState("");
  const [msa, setMsa] = useState("");
  const [loanMin, setLoanMin] = useState("0");
  const [loanMax, setLoanMax] = useState("0");
  const [termMin, setTermMin] = useState("0");
  const [termMax, setTermMax] = useState("0");
  const [status, setStatus] = useState<MarketDetail["status"]>("draft");
  const [zipRadiiRows, setZipRadiiRows] = useState<DisplayRow[] | null>(null);
  // True only once the operator edits the ZIP/radius editor. Hydration must not
  // flip this, so a name-only save omits zipRadii from the PUT (F-07-05).
  const [zipRadiiTouched, setZipRadiiTouched] = useState(false);
  const [loadedFor, setLoadedFor] = useState<string | null>(null);

  // ZipRadiusRows reports edits through this — distinct from the hydration
  // setState below, which leaves `zipRadiiTouched` false.
  const handleZipRadiiChange = (rows: DisplayRow[]) => {
    setZipRadiiRows(rows);
    setZipRadiiTouched(true);
  };

  // Hydrate exactly once per market — re-fetches (after save) update the
  // server-side fields via the merged display rows below, not the editor
  // baseline.
  useEffect(() => {
    if (!data) return;
    if (loadedFor === data.id) return;
    setName(data.name);
    setState(data.state);
    setMsa(data.msa ?? "");
    setLoanMin(String(data.loanBandMinUsd));
    setLoanMax(String(data.loanBandMaxUsd));
    setTermMin(String(data.termMonthsMin));
    setTermMax(String(data.termMonthsMax));
    setStatus(data.status);
    setZipRadiiRows((data.zipRadii ?? []).map((r) => ({ ...r })));
    setZipRadiiTouched(false);
    setLoadedFor(data.id);
  }, [data, loadedFor]);

  // For the map: prefer freshly fetched server rows so the operator sees
  // verified centroids immediately after save. The row editor stays on the
  // in-progress local state.
  const mapRows: ZipRadiusRowResponse[] = useMemo(() => {
    if (!data?.zipRadii) return [];
    if (!zipRadiiRows) return data.zipRadii;
    // Show server rows whose (zip,radiusMi) tuple is still present locally.
    const localKeys = new Set(zipRadiiRows.map((r) => `${r.zip}:${r.radiusMi}`));
    return data.zipRadii.filter((r) => localKeys.has(`${r.zip}:${r.radiusMi}`));
  }, [data?.zipRadii, zipRadiiRows]);

  const save = useMutation({
    mutationFn: (payload: MarketPayload) =>
      apiFetch<{ ok: true }>(`/api/settings/markets/${id}`, {
        method: "PUT",
        body: JSON.stringify(payload),
      }),
    onSuccess: async () => {
      toast.success("Market saved.");
      // Re-hydrate the editor from the refetched market so the rows persist
      // (and pick up geocoded lat/lng) instead of resetting to empty (F-07-02).
      setLoadedFor(null);
      setZipRadiiTouched(false);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["settings", "markets"] }),
        qc.invalidateQueries({ queryKey: ["settings", "markets", id] }),
      ]);
    },
    onError: (err) => {
      if (err instanceof ApiRequestError && err.status === 401) {
        void navigate({ to: `/login?redirect=${encodeURIComponent(pathname)}` });
        return;
      }
      toast.error(humanizeApiError(err).summary);
    },
  });

  const saveError = save.isError ? humanizeApiError(save.error) : null;

  const remove = useMutation({
    mutationFn: () => apiFetch<{ ok: true }>(`/api/settings/markets/${id}`, { method: "DELETE" }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["settings", "markets"] });
      await navigate({ to: "/settings/markets" });
    },
  });

  const { setTopbar } = useTopbar();
  const marketName = data?.name ?? "Market";
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "Markets", to: "/settings/markets" },
        { label: marketName },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar, marketName]);

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (error) return <p className="text-sm text-neg">Failed to load market.</p>;
  if (!data) return null;

  const rowsForEditor = zipRadiiRows ?? data.zipRadii ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-6 border-b border-rule pb-5">
        <div>
          <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">{data.name}</h1>
          <p className="text-sm text-muted">
            Edit geography, loan band, and loan types for this market. Changes apply to the next
            scan.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            if (confirm("Delete this market?")) remove.mutate();
          }}
          disabled={remove.isPending}
          className="rounded border border-rule bg-neg-bg px-3 py-1.5 text-sm font-semibold text-neg hover:opacity-80 disabled:opacity-50"
        >
          Delete market
        </button>
      </div>

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          // Emit zipRadii only if the operator has touched the editor; an
          // untouched editor preserves backend rows (omit the field). A
          // name-only edit must not include zipRadii (F-07-05).
          const incoming =
            zipRadiiTouched && zipRadiiRows
              ? zipRadiiRows.map((r) => ({ zip: r.zip.trim(), radiusMi: r.radiusMi }))
              : null;
          // Reject — don't silently drop — any row whose ZIP isn't exactly 5 digits.
          if (incoming !== null) {
            const bad = incoming.filter((r) => !/^\d{5}$/.test(r.zip));
            if (bad.length > 0) {
              const zips = bad.map((r) => `"${r.zip || "(empty)"}"`).join(", ");
              toast.error(`Invalid ZIP code(s): ${zips}. Each ZIP must be exactly 5 digits.`);
              return;
            }
          }
          const payload: MarketPayload = {
            name: name.trim(),
            state: state.trim().toUpperCase(),
            loanBandMinUsd: Number(loanMin),
            loanBandMaxUsd: Number(loanMax),
            termMonthsMin: Number(termMin),
            termMonthsMax: Number(termMax),
            status,
          };
          if (msa.trim()) payload.msa = msa.trim();
          if (incoming !== null) payload.zipRadii = incoming;
          save.mutate(payload);
        }}
      >
        <div className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-xs text-muted">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">State</span>
            <input
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
              maxLength={2}
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm uppercase text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as MarketDetail["status"])}
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
            </select>
          </label>
          <label className="col-span-2 flex flex-col gap-1">
            <span className="text-xs text-muted">MSA (optional)</span>
            <input
              value={msa}
              onChange={(e) => setMsa(e.target.value)}
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Loan min ($)</span>
            <input
              value={loanMin}
              onChange={(e) => setLoanMin(e.target.value)}
              type="number"
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Loan max ($)</span>
            <input
              value={loanMax}
              onChange={(e) => setLoanMax(e.target.value)}
              type="number"
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Term min (mo)</span>
            <input
              value={termMin}
              onChange={(e) => setTermMin(e.target.value)}
              type="number"
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted">Term max (mo)</span>
            <input
              value={termMax}
              onChange={(e) => setTermMax(e.target.value)}
              type="number"
              className="rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink focus:outline-none focus:ring-1 focus:ring-gold"
            />
          </label>
        </div>

        <ZipRadiusRows rows={rowsForEditor} onChange={handleZipRadiiChange} />

        <MarketMap rows={mapRows} />

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={save.isPending}
            className="rounded bg-gold px-4 py-2 text-sm font-semibold text-ink disabled:opacity-50"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
          {saveError ? (
            <span className="text-xs text-neg">{saveError.summary}</span>
          ) : save.isSuccess && !save.isPending ? (
            <span className="text-xs text-pos">Saved.</span>
          ) : null}
        </div>
      </form>
    </div>
  );
}
